import { api } from 'components/api'
import { inboxUsersSearchPath } from 'components/routes/api'
import { DEFAULT_AVATAR_URL } from 'components/inbox/utils'

export interface ApiInboxUserSearchResult {
  id: number
  name: string
  email: string | null
  type: string
  supplier_enabled: boolean
  review_author?: boolean
  organization_name?: string | null
  thumbnail?: string | null
}

interface ApiInboxUsersSearchResponse {
  users?: ApiInboxUserSearchResult[]
  reviewer_recipient?: ApiInboxUserSearchResult | null
}

export interface InboxUserSearchResult {
  id: number
  name: string
  email: string | null
  type: string
  supplierEnabled: boolean
  reviewAuthor: boolean
  organizationName: string
  avatarUrl: string
}

type FetchInboxUsersParams =
  | { reviewId: number, issueId?: never }
  | { issueId: number, reviewId?: never }

const fetchInboxUsersResponse = async (
  params: FetchInboxUsersParams,
  query = ''
): Promise<ApiInboxUsersSearchResponse> => {
  const response = await api.get<unknown, ApiInboxUsersSearchResponse>({
    url: inboxUsersSearchPath(),
    headers: { Accept: 'application/json' },
    cacheOptions: { enabled: false },
    params: {
      ...(params.reviewId == null ? {} : { review_id: params.reviewId }),
      ...(params.issueId == null ? {} : { issue_id: params.issueId }),
      query
    }
  })

  return response.data
}

const mapUser = (user: ApiInboxUserSearchResult): InboxUserSearchResult => ({
  id: user.id,
  name: user.name,
  email: user.email,
  type: user.type,
  supplierEnabled: user.supplier_enabled,
  reviewAuthor: user.review_author ?? false,
  organizationName: user.organization_name ?? '',
  avatarUrl: user.thumbnail ?? DEFAULT_AVATAR_URL
})

const fetchInboxUsers = async (
  params: FetchInboxUsersParams,
  query = ''
): Promise<InboxUserSearchResult[]> => {
  const response = await fetchInboxUsersResponse(params, query)

  if (response.users == null) {
    throw new Error('Inbox users response is missing users')
  }

  return response.users.map(mapUser)
}

export const fetchReviewInboxUsers = async (
  reviewId: number,
  query = ''
): Promise<InboxUserSearchResult[]> => await fetchInboxUsers({ reviewId }, query)

export const fetchIssueInboxUsers = async (
  issueId: number,
  query = ''
): Promise<InboxUserSearchResult[]> => await fetchInboxUsers({ issueId }, query)

export const fetchIssueReviewerRecipient = async (
  issueId: number
): Promise<InboxUserSearchResult | null> => {
  const response = await fetchInboxUsersResponse({ issueId })
  return response.reviewer_recipient == null ? null : mapUser(response.reviewer_recipient)
}
